import { API } from './api'
import { openPrint } from './StockAdmin'
import { useEffect, useState } from 'react'

/**
 * Vzhľad tlačovej sezónnej objednávky – panel v administrácii (Objednávky → 🎄 Vianoce).
 * Rovnaká myšlienka ako nastavenia cenoviek: mierka písma, hrúbka názvu a ceny, veľkosť fotiek
 * a počet stĺpcov. Platí spoločne pre tlač všetkých sezón. Ukladá sa cez
 * /api/admin/seasonal-order-form-settings; náhľad so vzorovými údajmi cez .../preview,
 * takže sa dá pozrieť aj bez rozpracovanej sezóny.
 */

type PhotoSize = 'OFF' | 'S' | 'M' | 'L'
type Cols = 1 | 2 | 3
type Settings = { primaryColor: string; textScale: number; nameBold: boolean; photoSize: PhotoSize; columnsA4: Cols; columnsA5: Cols }

const DEFAULTS: Settings = { primaryColor: '#b3262e', textScale: 100, nameBold: true, photoSize: 'M', columnsA4: 2, columnsA5: 1 }
const TEXT_SCALES = [
  { value: 85, label: 'Menšie (85 %)' },
  { value: 100, label: 'Normálne (100 %)' },
  { value: 115, label: 'Väčšie (115 %)' },
  { value: 130, label: 'Najväčšie (130 %)' },
]
const PHOTO_SIZES: Array<{ value: PhotoSize; label: string }> = [
  { value: 'OFF', label: 'Bez fotiek' }, { value: 'S', label: 'Malé' }, { value: 'M', label: 'Stredné' }, { value: 'L', label: 'Veľké' },
]

function isRecord(value: unknown): value is Record<string, unknown> { return typeof value === 'object' && value !== null }

export default function SeasonalOrderFormSettingsPanel() {
  const [settings, setSettings] = useState<Settings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true); setError('')
      try {
        const response = await fetch(`${API}/api/admin/seasonal-order-form-settings`, { credentials: 'include' })
        const data = await response.json().catch(() => null)
        if (response.status === 401) window.dispatchEvent(new Event('emili-admin-session-expired'))
        if (!response.ok || !isRecord(data) || !isRecord(data.settings)) throw new Error('Vzhľad tlačovej objednávky sa nepodarilo načítať.')
        if (!cancelled) setSettings({ ...DEFAULTS, ...data.settings } as Settings)
      } catch (reason) {
        if (!cancelled) { setSettings({ ...DEFAULTS }); setError(reason instanceof Error ? reason.message : 'Vzhľad tlačovej objednávky sa nepodarilo načítať.') }
      } finally { if (!cancelled) setLoading(false) }
    })()
    return () => { cancelled = true }
  }, [])

  async function save() {
    if (!settings) return
    setSaving(true); setError(''); setNotice('')
    try {
      const response = await fetch(`${API}/api/admin/seasonal-order-form-settings`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(settings) })
      const data = await response.json().catch(() => ({}))
      if (response.status === 401) window.dispatchEvent(new Event('emili-admin-session-expired'))
      if (!response.ok) throw new Error((isRecord(data) && typeof data.error === 'string' ? data.error : null) || 'Uloženie sa nepodarilo.')
      setNotice('Vzhľad je uložený. Prejaví sa na tlačových objednávkach všetkých sezón.')
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Uloženie sa nepodarilo.') }
    finally { setSaving(false) }
  }

  if (loading || !settings) return <details className="so-print-settings"><summary>🎨 Vzhľad tlačovej objednávky</summary><p className="empty-message">Načítavam…</p></details>

  const set = (patch: Partial<Settings>) => setSettings({ ...settings, ...patch })

  return <details className="so-print-settings">
    <summary>🎨 Vzhľad tlačovej objednávky</summary>
    <p className="so-hint">Platí pre tlačidlo „🖨 Tlačová objednávka“ pri sezóne vyššie. Počet stĺpcov si nastavte zvlášť pre A4 a pre A5, nech to na danom formáte pekne vyjde – je to predsa objednávka na tlač. Zmeny si uložte a potom skontrolujte tlačidlom náhľadu.</p>
    <div className="so-grid">
      <label>Hlavná farba (nadpis, kategórie)<input type="color" value={settings.primaryColor} onChange={e => set({ primaryColor: e.target.value })} /></label>
      <label>Veľkosť písma (názvy, ceny, nadpisy)<select value={settings.textScale} onChange={e => set({ textScale: Number(e.target.value) })}>{TEXT_SCALES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</select></label>
      <label>Počet stĺpcov na A4<select value={settings.columnsA4} onChange={e => set({ columnsA4: Number(e.target.value) as Cols })}><option value={1}>1 stĺpec</option><option value={2}>2 stĺpce</option><option value={3}>3 stĺpce</option></select></label>
      <label>Počet stĺpcov na A5<select value={settings.columnsA5} onChange={e => set({ columnsA5: Number(e.target.value) as Cols })}><option value={1}>1 stĺpec</option><option value={2}>2 stĺpce</option><option value={3}>3 stĺpce</option></select></label>
      <label>Veľkosť fotiek výrobkov<select value={settings.photoSize} onChange={e => set({ photoSize: e.target.value as PhotoSize })}>{PHOTO_SIZES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</select></label>
      <label className="so-check"><input type="checkbox" checked={settings.nameBold} onChange={e => set({ nameBold: e.target.checked })} /> Tučné písmo názvu aj ceny (lepšia čitateľnosť)</label>
    </div>
    {error && <p className="error-message" role="alert">{error}</p>}
    {notice && <p className="success-message" role="status">{notice}</p>}
    <div className="so-print-settings-actions">
      <button type="button" className="pa-ghost" onClick={() => openPrint('/api/admin/seasonal-order-form-settings/preview?format=a5')}>👁 Náhľad A5</button>
      <button type="button" className="pa-ghost" onClick={() => openPrint('/api/admin/seasonal-order-form-settings/preview?format=a4')}>👁 Náhľad A4</button>
      <button type="button" className="primary-btn" disabled={saving} onClick={() => void save()}>{saving ? 'Ukladám…' : 'Uložiť vzhľad'}</button>
    </div>
  </details>
}
